import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, User, Store, Link, Check, Globe, ShoppingBag, ToggleLeft, ToggleRight, Hash, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useStoreConfig } from '../hooks/useStoreConfig';
import { useEcommercePlatforms, EcommercePlatform } from '../hooks/useEcommercePlatforms';
import { useSocialMediaPlatforms, SocialMediaPlatform } from '../hooks/useSocialMediaPlatforms';
import { useSeoAndHashtags } from '../hooks/useSeoAndHashtags';
import cloudFunctionService from '../services/cloudFunctionService';
import LoginForm from './auth/LoginForm';
import GoogleButton from './auth/GoogleButton';
import PlatformConnectionModal from './PlatformConnectionModal';
import SocialMediaConnectionModal from './SocialMediaConnectionModal';
import IntegrationsDocumentationModal from './IntegrationsDocumentationModal';

interface AccountManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}


const AccountManagementModal: React.FC<AccountManagementModalProps> = ({ isOpen, onClose }) => {
  const { user, checkUserSignInMethods, linkGoogleAccountWithVerification } = useAuth();
  const { t } = useI18n();
  const { storeConfig } = useStoreConfig();
  const { platforms, activePlatforms, loading: platformsLoading, updatePlatform, savePlatformSettings, loadPlatformSettings } = useEcommercePlatforms();
  const { platforms: socialPlatforms, activePlatforms: activeSocialPlatforms, updatePlatform: updateSocialPlatform, savePlatformSettings: saveSocialPlatformSettings, loadPlatformSettings: loadSocialPlatformSettings } = useSocialMediaPlatforms();
  const { seoKeywords, hashtags, addSeoKeyword, removeSeoKeyword, addHashtag, removeHashtag, predefinedSeoKeywords, predefinedHashtags } = useSeoAndHashtags();
  const [hasLinkedAccount, setHasLinkedAccount] = useState(false);
  const [linkingAccount, setLinkingAccount] = useState(false);
  const [activeTab, setActiveTab] = useState<'platforms' | 'social' | 'accounts' | 'store' | 'seo'>('platforms');
  const [showPlatformConnection, setShowPlatformConnection] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<EcommercePlatform | null>(null);
  const [showSocialConnection, setShowSocialConnection] = useState(false);
  const [selectedSocialPlatform, setSelectedSocialPlatform] = useState<SocialMediaPlatform | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [lockedPlatformMessage, setLockedPlatformMessage] = useState<string | null>(null);

  // Estados locales para inputs
  const [newSeoKeyword, setNewSeoKeyword] = useState('');
  const [newHashtag, setNewHashtag] = useState('');


  // Ocultar mensaje de plataforma bloqueada después de 3 segundos
  useEffect(() => {
    if (lockedPlatformMessage) {
      const timer = setTimeout(() => {
        setLockedPlatformMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [lockedPlatformMessage]);

  // Verificar estado de vinculación de cuentas
  useEffect(() => {
    const checkAccountLinking = async () => {
      if (user?.email) {
        try {
          const hasEmailPassword = user.providerData.some((p: any) => p.providerId === 'password');
          const hasGoogle = user.providerData.some((p: any) => p.providerId === 'google.com');
          
          if (hasEmailPassword && hasGoogle) {
            setHasLinkedAccount(true);
          } else {
            const methods = await checkUserSignInMethods(user.email);
            const hasEmailPasswordAPI = methods.includes('password');
            const hasGoogleAPI = methods.includes('google.com');
            setHasLinkedAccount(hasEmailPasswordAPI && hasGoogleAPI);
          }
        } catch (error) {
          console.error('Error verificando vinculación de cuentas:', error);
          setHasLinkedAccount(false);
        }
      } else {
        setHasLinkedAccount(false);
      }
    };

    checkAccountLinking();
  }, [user, checkUserSignInMethods]);

  // Función para verificar conexiones
  const handleVerifyConnections = async () => {
    try {
      console.log('🔍 Verificando conexiones automáticamente...');
      
      // Recargar configuraciones desde Firebase
      await loadPlatformSettings();
      await loadSocialPlatformSettings();
      
      console.log('✅ Verificación de conexiones completada');
    } catch (error) {
      console.error('❌ Error verificando conexiones:', error);
    }
  };

  // Verificar conexiones automáticamente cuando se abre el modal
  // useEffect problemático eliminado - causaba reseteo del estado al abrir el modal
  // useEffect(() => {
  //   if (isOpen) {
  //     handleVerifyConnections();
  //   }
  // }, [isOpen]);

  const togglePlatform = async (platformId: string) => {
    const platform = platforms.find(p => p.id === platformId);
    if (platform) {
      // Si la plataforma está bloqueada, mostrar mensaje
      if (platform.locked) {
        setLockedPlatformMessage(`¡Opa! ${platform.name} todavía no está disponible, pero pronto va a estar lista. ¡Aguantá un toque!`);
        return;
      }
      
      const newEnabledState = !platform.enabled;
      await updatePlatform(platformId, { enabled: newEnabledState });
      
      // Si se está habilitando una plataforma, abrir modal de conexión
      if (newEnabledState && !platform.connected) {
        openPlatformConnection(platformId);
      }
      
      // Si se está deshabilitando una plataforma conectada, desvincular automáticamente
      if (!newEnabledState && platform.connected) {
        await unlinkPlatformAutomatically(platformId);
      }
    }
  };

  const openPlatformConnection = (platformId: string) => {
    const platform = platforms.find(p => p.id === platformId);
    if (platform) {
      setSelectedPlatform(platform);
      setShowPlatformConnection(true);
    }
  };

  const handleConnectionSuccess = async (platformId: string, credentials: any) => {
    try {
      console.log(`✅ Conexión exitosa para ${platformId}:`, credentials);
      console.log(`✅ Client ID recibido:`, credentials.clientId);
      console.log(`✅ Client Secret recibido:`, credentials.clientSecret);
      
      // Verificar que el usuario esté autenticado
      if (!user) {
        console.error('❌ Usuario no autenticado, no se puede guardar la configuración');
        setError('Debes iniciar sesión para guardar la configuración de la plataforma');
        return;
      }
      
      console.log('✅ Usuario autenticado:', user.uid);
      
      // Actualizar el estado de la plataforma como conectada
      const updateData = { 
        connected: true,
        apiKey: credentials.clientId || credentials.apiKey || credentials.access_token,
        apiSecret: credentials.clientSecret || credentials.refresh_token
      };
      console.log(`✅ Datos a actualizar:`, updateData);
      
      await updatePlatform(platformId, updateData);

      // Guardar las configuraciones
      console.log('💾 Guardando configuraciones...');
      await savePlatformSettings();
      console.log('💾 Configuraciones guardadas');

      // NO recargar - confiar en que se guardó correctamente
      console.log('✅ Configuraciones guardadas - NO recargando para evitar sobrescritura');

      // Verificar el estado actual después de recargar
      const updatedPlatform = platforms.find(p => p.id === platformId);
      console.log(`🔍 Estado actualizado de ${platformId}:`, {
        enabled: updatedPlatform?.enabled,
        connected: updatedPlatform?.connected,
        apiKey: updatedPlatform?.apiKey ? '✅ Presente' : '❌ Ausente'
      });
      
      // Debug: verificar todas las plataformas
      console.log('🔍 Todas las plataformas después de recargar:', platforms.map(p => ({
        id: p.id,
        enabled: p.enabled,
        connected: p.connected,
        apiKey: p.apiKey ? '✅ Presente' : '❌ Ausente'
      })));

      console.log(`🎉 Plataforma ${platformId} conectada exitosamente`);
    } catch (error) {
      console.error(`❌ Error guardando conexión para ${platformId}:`, error);
      // Si hay error, revertir el estado
      await updatePlatform(platformId, { connected: false });
    }
  };

  const handleConnectionError = async (platformId: string) => {
    try {
      console.log(`❌ Error en conexión para ${platformId}, limpiando estado...`);
      
      // Asegurar que la plataforma esté deshabilitada y desconectada
      await updatePlatform(platformId, { 
        enabled: false,
        connected: false,
        apiKey: undefined,
        apiSecret: undefined
      });

      // Guardar las configuraciones
      await savePlatformSettings();

      // Recargar datos desde Firebase para sincronizar
      await loadPlatformSettings();

      console.log(`🧹 Estado limpiado para ${platformId}`);
    } catch (error) {
      console.error(`❌ Error limpiando estado para ${platformId}:`, error);
    }
  };

  const openSocialConnection = (platformId: string) => {
    const platform = socialPlatforms.find(p => p.id === platformId);
    if (platform) {
      setSelectedSocialPlatform(platform);
      setShowSocialConnection(true);
    }
  };

  const handleSocialConnectionSuccess = async (platformId: string, credentials: any) => {
    try {
      console.log(`✅ Conexión exitosa para red social ${platformId}:`, credentials);
      
      // Actualizar el estado de la red social como conectada
      await updateSocialPlatform(platformId, { 
        connected: true,
        accessToken: credentials.access_token,
        refreshToken: credentials.refresh_token
      });

      // Guardar las configuraciones
      await saveSocialPlatformSettings();

      // Recargar datos desde Firebase para sincronizar
      await loadSocialPlatformSettings();

      console.log(`🎉 Red social ${platformId} conectada exitosamente`);
    } catch (error) {
      console.error(`❌ Error guardando conexión para red social ${platformId}:`, error);
      // Si hay error, revertir el estado
      await updateSocialPlatform(platformId, { connected: false });
    }
  };

  const handleSocialConnectionError = async (platformId: string) => {
    try {
      console.log(`❌ Error en conexión para red social ${platformId}, limpiando estado...`);
      
      // Asegurar que la red social esté deshabilitada y desconectada
      await updateSocialPlatform(platformId, { 
        enabled: false,
        connected: false,
        accessToken: undefined,
        refreshToken: undefined
      });

      // Guardar las configuraciones
      await saveSocialPlatformSettings();

      // Recargar datos desde Firebase para sincronizar
      await loadSocialPlatformSettings();

      console.log(`🧹 Estado limpiado para red social ${platformId}`);
    } catch (error) {
      console.error(`❌ Error limpiando estado para red social ${platformId}:`, error);
    }
  };

  const toggleSocialPlatform = async (platformId: string) => {
    const platform = socialPlatforms.find(p => p.id === platformId);
    if (platform) {
      // Si la plataforma está bloqueada, mostrar mensaje
      if (platform.locked) {
        setLockedPlatformMessage(`¡Opa! ${platform.name} todavía no está disponible, pero pronto va a estar lista. ¡Aguantá un toque!`);
        return;
      }
      
      const newEnabledState = !platform.enabled;
      await updateSocialPlatform(platformId, { enabled: newEnabledState });
      
      // Si se está habilitando una red social, abrir modal de conexión
      if (newEnabledState && !platform.connected) {
        openSocialConnection(platformId);
      }
      
      // Si se está deshabilitando una red social conectada, desvincular automáticamente
      if (!newEnabledState && platform.connected) {
        await unlinkPlatformAutomatically(platformId);
      }
    }
  };


  const unlinkPlatformAutomatically = async (platformId: string) => {
    try {
      console.log(`🔓 Desvinculando plataforma ${platformId} automáticamente...`);
      
      // Usar Cloud Function para desvincular plataforma
      const result = await cloudFunctionService.unlinkPlatform(platformId);
      
      if (result.success) {
        // Actualizar estado local
        const ecommercePlatform = platforms.find(p => p.id === platformId);
        const socialPlatform = socialPlatforms.find(p => p.id === platformId);
        
        if (ecommercePlatform) {
          await updatePlatform(platformId, { enabled: false, connected: false });
        } else if (socialPlatform) {
          await updateSocialPlatform(platformId, { enabled: false, connected: false });
        }

        // Guardar configuraciones
        await savePlatformSettings();
        await saveSocialPlatformSettings();

        // Recargar datos desde Firebase para sincronizar
        await loadPlatformSettings();
        await loadSocialPlatformSettings();

        console.log(`✅ Plataforma ${platformId} desvinculada automáticamente`);
      }
    } catch (error) {
      console.error(`❌ Error desvinculando plataforma ${platformId}:`, error);
    }
  };


  const handleLinkGoogleAccount = async () => {
    setLinkingAccount(true);
    try {
      await linkGoogleAccountWithVerification();
      
      if (user?.email) {
        const hasEmailPassword = user.providerData.some((p: any) => p.providerId === 'password');
        const hasGoogle = user.providerData.some((p: any) => p.providerId === 'google.com');
        
        if (hasEmailPassword && hasGoogle) {
          setHasLinkedAccount(true);
        } else {
          const methods = await checkUserSignInMethods(user.email);
          const hasEmailPasswordAPI = methods.includes('password');
          const hasGoogleAPI = methods.includes('google.com');
          setHasLinkedAccount(hasEmailPasswordAPI && hasGoogleAPI);
        }
      }
    } catch (error) {
      console.error('Error vinculando cuenta de Google:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      alert(`Error vinculando cuenta: ${errorMessage}`);
    } finally {
      setLinkingAccount(false);
    }
  };

  const handleSaveSettings = async () => {
    await savePlatformSettings();
    onClose();
  };

  const handleAddSeoKeyword = () => {
    if (newSeoKeyword.trim()) {
      addSeoKeyword(newSeoKeyword);
      setNewSeoKeyword('');
    }
  };

  const handleAddHashtag = () => {
    if (newHashtag.trim()) {
      addHashtag(newHashtag);
      setNewHashtag('');
    }
  };

  const tabs = [
    { id: 'platforms', label: t('ecommerceTab'), icon: ShoppingBag },
    { id: 'social', label: t('socialMediaTab'), icon: Globe },
    { id: 'accounts', label: t('accountsTab'), icon: Link },
    { id: 'store', label: t('storeTab'), icon: Store },
    { id: 'seo', label: 'SEO & Hashtags', icon: Hash }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                  <User className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {t('accountManagementTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t('accountManagementSubtitle')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Mensaje de plataforma bloqueada */}
            <AnimatePresence>
              {lockedPlatformMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mx-6 mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl"
                >
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium text-center">
                    {lockedPlatformMessage}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-slate-700">
              <div className="flex space-x-1 p-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Icon size={18} />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {activeTab === 'platforms' && (
                <div className="space-y-4">
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('ecommercePlatformsTitle')}
                      </h3>
                      {activePlatforms.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                            {activePlatforms.length} {activePlatforms.length !== 1 ? t('activePlural') : t('active')}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t('ecommercePlatformsDescriptionModal')}
                    </p>
                    {activePlatforms.length > 0 && (
                      <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                        <p className="text-sm text-green-800 dark:text-green-200">
                          <strong>{t('platformsReadyForPublishing')}:</strong> {activePlatforms.map(p => p.name).join(', ')}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          {t('platformsReadyDescription')}
                        </p>
                      </div>
                    )}
                    {platforms.filter(p => p.enabled && !p.connected).length > 0 && (
                      <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl">
                        <p className="text-sm text-orange-800 dark:text-orange-200">
                          <strong>{t('platformsPendingConfiguration')}:</strong> {platforms.filter(p => p.enabled && !p.connected).map(p => p.name).join(', ')}
                        </p>
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                          {t('platformsPendingDescription')}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-4">
                    {platforms.map((platform) => (
                      <motion.div
                        key={platform.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-4 rounded-xl border transition-all duration-200 ${
                          platform.locked
                            ? 'border-gray-300 bg-gray-100 dark:bg-slate-800/50 dark:border-slate-600 opacity-60'
                            : platform.enabled
                            ? 'border-primary-200 bg-primary-50 dark:bg-slate-700 dark:border-primary-800'
                            : 'border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">{platform.icon}</span>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className={`font-medium ${
                                  platform.enabled 
                                    ? 'text-gray-900 dark:text-white' 
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                  {platform.name}
                                </h4>
                                {platform.locked && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                                    Próximamente
                                  </span>
                                )}
                                {platform.enabled && !platform.connected && (
                                  <div className="flex items-center space-x-1">
                                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                                    <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                      {t('needsConfiguration')}
                                    </span>
                                  </div>
                                )}
                                {platform.connected && (
                                  <div className="flex items-center space-x-1">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                      {t('connected')}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <p className={`text-sm ${
                                platform.enabled 
                                  ? 'text-gray-700 dark:text-gray-200' 
                                  : 'text-gray-600 dark:text-gray-300'
                              }`}>
                                {platform.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {platform.enabled && !platform.connected && (
                              <button
                                onClick={() => openPlatformConnection(platform.id)}
                                className="px-3 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors duration-200"
                              >
                                {t('linkAccount')}
                              </button>
                            )}
                            {platform.enabled && platform.connected && (
                              <button
                                onClick={() => unlinkPlatformAutomatically(platform.id)}
                                className="px-3 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors duration-200"
                              >
                                {t('unlink')}
                              </button>
                            )}
                            <button
                              onClick={() => togglePlatform(platform.id)}
                              className="flex items-center space-x-2"
                            >
                              {platform.enabled ? (
                                <ToggleRight className="w-8 h-8 text-primary-600" />
                              ) : (
                                <ToggleLeft className="w-8 h-8 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'social' && (
                <div className="space-y-4">
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('socialMediaTitle')}
                      </h3>
                      {activeSocialPlatforms.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                            {activeSocialPlatforms.length} {activeSocialPlatforms.length !== 1 ? t('activePlural') : t('active')}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t('socialMediaDescriptionModal')}
                    </p>
                    {activeSocialPlatforms.length > 0 && (
                      <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                        <p className="text-sm text-green-800 dark:text-green-200">
                          <strong>{t('socialMediaReady')}:</strong> {activeSocialPlatforms.map(p => p.name).join(', ')}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          {t('socialMediaReadyDescription')}
                        </p>
                      </div>
                    )}
                    {socialPlatforms.filter(p => p.enabled && !p.connected).length > 0 && (
                      <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl">
                        <p className="text-sm text-orange-800 dark:text-orange-200">
                          <strong>{t('socialMediaPendingConfiguration')}:</strong> {socialPlatforms.filter(p => p.enabled && !p.connected).map(p => p.name).join(', ')}
                        </p>
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                          {t('socialMediaPendingDescription')}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-4">
                    {socialPlatforms.map((platform) => (
                      <motion.div
                        key={platform.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-4 rounded-xl border transition-all duration-200 ${
                          platform.locked
                            ? 'border-gray-300 bg-gray-100 dark:bg-slate-800/50 dark:border-slate-600 opacity-60'
                            : platform.enabled
                            ? 'border-primary-200 bg-primary-50 dark:bg-slate-700 dark:border-primary-800'
                            : 'border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">{platform.icon}</span>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className={`font-medium ${
                                  platform.enabled 
                                    ? 'text-gray-900 dark:text-white' 
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                  {platform.name}
                                </h4>
                                {platform.locked && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                                    Próximamente
                                  </span>
                                )}
                                {platform.enabled && !platform.connected && (
                                  <div className="flex items-center space-x-1">
                                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                                    <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                      {t('needsConfiguration')}
                                    </span>
                                  </div>
                                )}
                                {platform.connected && (
                                  <div className="flex items-center space-x-1">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                      {t('connected')}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <p className={`text-sm ${
                                platform.enabled 
                                  ? 'text-gray-700 dark:text-gray-200' 
                                  : 'text-gray-600 dark:text-gray-300'
                              }`}>
                                {platform.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {platform.enabled && !platform.connected && (
                              <button
                                onClick={() => openSocialConnection(platform.id)}
                                className="px-3 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors duration-200"
                              >
                                {t('linkAccount')}
                              </button>
                            )}
                            {platform.enabled && platform.connected && (
                              <button
                                onClick={() => unlinkPlatformAutomatically(platform.id)}
                                className="px-3 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors duration-200"
                              >
                                {t('unlink')}
                              </button>
                            )}
                            <button
                              onClick={() => toggleSocialPlatform(platform.id)}
                              className="flex items-center space-x-2"
                            >
                              {platform.enabled ? (
                                <ToggleRight className="w-8 h-8 text-primary-600" />
                              ) : (
                                <ToggleLeft className="w-8 h-8 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'accounts' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('accountConnectionTitle')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t('accountConnectionDescription')}
                    </p>
                  </div>

                  {user ? (
                    <div className="space-y-4">
                      {hasLinkedAccount ? (
                        <div className="p-4 rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
                          <div className="flex items-center space-x-3">
                            <Check className="w-6 h-6 text-green-600" />
                            <div>
                              <h4 className="font-medium text-green-900 dark:text-green-100">
                                {t('accountLinked')}
                              </h4>
                              <p className="text-sm text-green-700 dark:text-green-300">
                                {t('accountLinkedDescription')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="p-4 rounded-xl border border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800">
                            <div className="flex items-center space-x-3 mb-4">
                              <Link className="w-6 h-6 text-orange-600" />
                              <div>
                                <h4 className="font-medium text-orange-900 dark:text-orange-100">
                                  {t('linkGoogleAccountTitle')}
                                </h4>
                                <p className="text-sm text-orange-700 dark:text-orange-300">
                                  {t('linkGoogleAccountDescriptionModal')}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={handleLinkGoogleAccount}
                              disabled={linkingAccount}
                              className="w-full bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-700 font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-3 disabled:opacity-50"
                            >
                              <img src="https://www.gstatic.com/images/branding/product/1x/gsa_64dp.png" alt="Google" className="w-5 h-5" />
                              <span>{linkingAccount ? t('linkingAccountModal') : t('linkWithGoogle')}</span>
                            </button>
                          </div>

                          <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
                            <div className="flex items-center space-x-3 mb-4">
                              <Globe className="w-6 h-6 text-gray-600" />
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                  {t('signInWithEmail')}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                  {t('currentAuthenticationMethod')}
                                </p>
                              </div>
                            </div>
                            <LoginForm onSuccess={() => {}} />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
                        <div className="flex items-center space-x-3 mb-4">
                          <Globe className="w-6 h-6 text-gray-600" />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">
                              {t('signInWithEmail')}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {t('signInWithEmailDescription')}
                            </p>
                          </div>
                        </div>
                        <LoginForm onSuccess={() => {}} />
                      </div>

                      <div className="my-4 text-center text-gray-500">o</div>

                      <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
                        <div className="flex items-center space-x-3 mb-4">
                          <img src="https://www.gstatic.com/images/branding/product/1x/gsa_64dp.png" alt="Google" className="w-6 h-6" />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">
                              {t('signInWithGoogleTitle')}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {t('signInWithGoogleDescription')}
                            </p>
                          </div>
                        </div>
                        <GoogleButton onSuccess={() => {}} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'store' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('storeConfigurationTitle')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t('storeConfigurationDescription')}
                    </p>
                  </div>

                  {storeConfig ? (
                    <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
                      <div className="flex items-center space-x-3">
                        <Store className="w-6 h-6 text-blue-600" />
                        <div>
                          <h4 className="font-medium text-blue-900 dark:text-blue-100">
                            {t('storeType')}: {storeConfig.name}
                          </h4>
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            {t('currentStoreConfiguration')}
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            {t('storeSetupDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
                      <div className="flex items-center space-x-3">
                        <Store className="w-6 h-6 text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">
                            {t('noStoreConfiguration')}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {t('configureStoreFromMain')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'seo' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      SEO & Hashtags Predefinidos
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Configura palabras clave SEO para tus productos y hashtags para tus publicaciones en redes sociales.
                    </p>
                  </div>

                  {/* Sección de Palabras SEO */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Search className="w-5 h-5 text-blue-600" />
                      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Palabras SEO para "Publicar en Mis Tiendas"
                      </h4>
                    </div>
                    
                    <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
                      <div className="space-y-3">
                        {/* Input para agregar nueva palabra SEO */}
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={newSeoKeyword}
                            onChange={(e) => setNewSeoKeyword(e.target.value)}
                            placeholder="Agregar palabra SEO..."
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddSeoKeyword()}
                          />
                          <button
                            onClick={handleAddSeoKeyword}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                          >
                            Agregar
                          </button>
                        </div>

                        {/* Desplegable con palabras predefinidas */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Palabras predefinidas:
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {predefinedSeoKeywords.map((keyword) => (
                              <button
                                key={keyword}
                                onClick={() => addSeoKeyword(keyword)}
                                disabled={seoKeywords.includes(keyword)}
                                className={`px-3 py-1 text-sm rounded-lg transition-colors duration-200 ${
                                  seoKeywords.includes(keyword)
                                    ? 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                    : 'bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                                }`}
                              >
                                {keyword}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Lista de palabras SEO agregadas */}
                        {seoKeywords.length > 0 && (
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Palabras SEO seleccionadas:
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {seoKeywords.map((keyword) => (
                                <div
                                  key={keyword}
                                  className="flex items-center space-x-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-lg"
                                >
                                  <span className="text-sm">{keyword}</span>
                                  <button
                                    onClick={() => removeSeoKeyword(keyword)}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sección de Hashtags */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Hash className="w-5 h-5 text-pink-600" />
                      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Hashtags para "Publicar en Mis Redes"
                      </h4>
                    </div>
                    
                    <div className="p-4 rounded-xl border border-pink-200 bg-pink-50 dark:bg-pink-900/20 dark:border-pink-800">
                      <div className="space-y-3">
                        {/* Input para agregar nuevo hashtag */}
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={newHashtag}
                            onChange={(e) => setNewHashtag(e.target.value)}
                            placeholder="Agregar hashtag..."
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddHashtag()}
                          />
                          <button
                            onClick={handleAddHashtag}
                            className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors duration-200"
                          >
                            Agregar
                          </button>
                        </div>

                        {/* Desplegable con hashtags predefinidos */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Hashtags predefinidos:
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {predefinedHashtags.map((hashtag) => (
                              <button
                                key={hashtag}
                                onClick={() => addHashtag(hashtag)}
                                disabled={hashtags.includes(hashtag)}
                                className={`px-3 py-1 text-sm rounded-lg transition-colors duration-200 ${
                                  hashtags.includes(hashtag)
                                    ? 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                    : 'bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-pink-50 dark:hover:bg-pink-900/30'
                                }`}
                              >
                                {hashtag}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Lista de hashtags agregados */}
                        {hashtags.length > 0 && (
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Hashtags seleccionados:
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {hashtags.map((hashtag) => (
                                <div
                                  key={hashtag}
                                  className="flex items-center space-x-1 px-3 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200 rounded-lg"
                                >
                                  <span className="text-sm">{hashtag}</span>
                                  <button
                                    onClick={() => removeHashtag(hashtag)}
                                    className="text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:hover:text-pink-200"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-8 border-t border-gray-200 dark:border-slate-700">
              {/* Flexbox: Documentation Link (left) + Action Buttons (right) */}
              <div className="flex justify-between items-center">
                {/* Documentation Link */}
                <button
                  onClick={() => setShowDocumentation(true)}
                  className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                >
                  <Globe className="w-4 h-4" />
                  <span>{t('viewIntegrationsDocumentation')}</span>
                </button>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={onClose}
                    className="px-6 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors duration-200"
                  >
                    {t('cancelModal')}
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    className="px-6 py-3 bg-primary-600 text-white hover:bg-primary-700 rounded-xl transition-colors duration-200 font-medium"
                  >
                    {t('saveConfiguration')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Platform Connection Modal */}
      <PlatformConnectionModal
        isOpen={showPlatformConnection}
        onClose={() => setShowPlatformConnection(false)}
        platform={selectedPlatform}
        onConnectionSuccess={handleConnectionSuccess}
        onConnectionError={() => selectedPlatform && handleConnectionError(selectedPlatform.id)}
      />

      {/* Social Media Connection Modal */}
      <SocialMediaConnectionModal
        isOpen={showSocialConnection}
        onClose={() => setShowSocialConnection(false)}
        platform={selectedSocialPlatform}
        onConnectionSuccess={handleSocialConnectionSuccess}
        onConnectionError={() => selectedSocialPlatform && handleSocialConnectionError(selectedSocialPlatform.id)}
      />

      {/* Integrations Documentation Modal */}
      <IntegrationsDocumentationModal
        isOpen={showDocumentation}
        onClose={() => setShowDocumentation(false)}
      />
    </AnimatePresence>
  );
};

export default AccountManagementModal;
